"use client";

import { SkeletonBar, SkeletonPulse } from "@/components/ui/skeleton";
import { CATALOG_PAGE_SIZES, type CatalogPageSize } from "@/lib/host-panel/catalog-query";

/**
 * The catalog's pagination, in the canon's own model (filters spec §7 and D-01).
 *
 * Wide viewports read "Página X de N" beside four controls of 44×44 — first, previous, next,
 * last — instead of the numbered buttons the prototype drew, which the canon does not have.
 * Narrow ones read "Exibindo X de N" over a bar that fills, and grow the grid by pressing
 * "Carregar mais": the low-density model the canon asks for on a phone.
 *
 * Other screens reuse it with their own page sizes and noun (`pageSizes`, `countLabel`); left
 * out, both fall back to the catalog's own, so the host panel renders exactly as before.
 */

export type CatalogPaginationProps<TSize extends number = CatalogPageSize> = {
  page: number;
  totalPages: number;
  /** How many the person is already seeing, and how many there are in all. */
  shown: number;
  totalItems: number;
  pageSize: TSize;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: TSize) => void;
  onLoadMore: () => void;
  isLoading?: boolean;
  /**
   * The totals are not this slice's yet (its page is on the way): every number that reads them is
   * a skeleton and the page controls wait, since the last page they would aim at is not known.
   * Off by default, so a screen that reads a page it already holds (the admin's report) is
   * drawn as it always was.
   */
  isCountPending?: boolean;
  /** The "Linhas por página" options, in order. Defaults to the catalog's `CATALOG_PAGE_SIZES`. */
  pageSizes?: readonly TSize[];
  /** Names the total ("732 peças", "128 listas"). Defaults to `piecesLabel`. */
  countLabel?: (count: number) => string;
};

const CONTROL_CLASS =
  "grid size-[var(--tb-touch-min)] cursor-pointer place-content-center border border-[var(--tb-border-strong)] text-[var(--fg)] transition-colors duration-[var(--tb-duration-2)] ease-[var(--tb-ease-linen)] hover:border-[var(--fg)] hover:bg-[var(--fg)] hover:text-[var(--tb-porcelain)] disabled:cursor-default disabled:opacity-35 disabled:hover:bg-transparent disabled:hover:text-[var(--fg)]";

/** A number that is not known yet: the line's own height, so nothing moves when it arrives. */
function PendingCount({ width }: { width: number }) {
  return (
    <span aria-busy="true" data-testid="pagination-count-skeleton" className="flex h-[18px] items-center">
      <SkeletonPulse>
        <SkeletonBar height={12} width={width} />
      </SkeletonPulse>
    </span>
  );
}

/** "3.619 peças", with the singular the canon asks for. */
export function piecesLabel(count: number): string {
  return `${count.toLocaleString("pt-BR")} ${count === 1 ? "peça" : "peças"}`;
}

export function CatalogPagination<TSize extends number = CatalogPageSize>({
  page,
  totalPages,
  shown,
  totalItems,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onLoadMore,
  isLoading = false,
  isCountPending = false,
  // Without `pageSizes` the caller's size type is the catalog's own (the generic's default).
  pageSizes = CATALOG_PAGE_SIZES as readonly number[] as readonly TSize[],
  countLabel = piecesLabel,
}: CatalogPaginationProps<TSize>) {
  if (totalItems === 0) return null;

  const isFirst = page <= 1;
  const isLast = page >= totalPages;
  // The range is read off what is on screen, not off the page number alone: "Carregar mais"
  // stacks pages (`shown` counts them all) and the last page is short. Both ends stay inside
  // the total, so rotating a phone that stacked four pages reads "1 a 80", never "61 a 140".
  const lastShown = shown === 0 ? 0 : Math.min(page * pageSize, totalItems);
  const firstShown = shown === 0 ? 0 : Math.max(1, lastShown - shown + 1);

  return (
    <div className="mt-[clamp(28px,4vw,48px)] px-[clamp(20px,4vw,56px)]">
      {/* Wide: page of pages, with the four controls. */}
      <div className="hidden items-center justify-between gap-4 [@media(min-width:1024px)]:flex">
        <span className="font-mono text-[12px] text-[var(--fg-muted)]">
          {isCountPending ? <PendingCount width={148} /> : `${firstShown} a ${lastShown} de ${countLabel(totalItems)}`}
        </span>

        <div className="flex items-center gap-3.5">
          <label className="flex items-center gap-2 text-[12px] text-[var(--fg-muted)]">
            Linhas por página
            <select
              value={pageSize}
              onChange={(event) => onPageSizeChange(Number(event.target.value) as TSize)}
              className="min-h-[var(--tb-touch-min)] cursor-pointer border border-[var(--tb-border-strong)] bg-transparent px-2.5 font-mono text-[12px] text-[var(--fg)] outline-none"
            >
              {pageSizes.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>

          <span className="font-mono text-[12px] text-[var(--fg)]">
            {isCountPending ? <PendingCount width={84} /> : `Página ${page} de ${totalPages}`}
          </span>

          <div className="flex items-center gap-1.5">
            <button type="button" className={CONTROL_CLASS} disabled={isFirst || isCountPending} aria-label="Primeira página" onClick={() => onPageChange(1)}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M18 6l-6 6 6 6M9 6v12" />
              </svg>
            </button>
            <button type="button" className={CONTROL_CLASS} disabled={isFirst || isCountPending} aria-label="Página anterior" onClick={() => onPageChange(page - 1)}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M15 6l-6 6 6 6" />
              </svg>
            </button>
            <button type="button" className={CONTROL_CLASS} disabled={isLast || isCountPending} aria-label="Próxima página" onClick={() => onPageChange(page + 1)}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M9 6l6 6-6 6" />
              </svg>
            </button>
            <button type="button" className={CONTROL_CLASS} disabled={isLast || isCountPending} aria-label="Última página" onClick={() => onPageChange(totalPages)}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M6 6l6 6-6 6M15 6v12" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Narrow: how much of the whole is on screen, and one way forward. */}
      <div className="flex flex-col items-center gap-3.5 [@media(min-width:1024px)]:hidden">
        <span className="font-mono text-[12px] text-[var(--fg-muted)]">
          {isCountPending ? <PendingCount width={132} /> : `Exibindo ${shown} de ${countLabel(totalItems)}`}
        </span>

        <span
          aria-hidden="true"
          className="block h-px w-full max-w-[220px] bg-[var(--tb-hairline)]"
        >
          <span
            className="block h-px bg-tb-green transition-[width] duration-[var(--tb-duration-3)] ease-[var(--tb-ease-linen)]"
            // Empty while the total is not known: a bar filled against the wrong total is a number too.
            style={{ width: isCountPending ? "0%" : `${Math.min(100, Math.round((shown / totalItems) * 100))}%` }}
          />
        </span>

        {shown < totalItems ? (
          <button
            type="button"
            onClick={onLoadMore}
            disabled={isLoading}
            /* `hover:enabled:`: while the page loads the button is disabled, and a disabled
               control must not answer the pointer (ruling of 2026-10-06). */
            className="min-h-[var(--tb-touch-min)] cursor-pointer border border-[var(--tb-border-strong)] px-[22px] py-3 text-[12px] font-bold uppercase leading-[normal] tracking-[0.14em] text-[var(--fg)] transition-colors duration-[var(--tb-duration-2)] ease-[var(--tb-ease-linen)] hover:enabled:border-[var(--fg)] hover:enabled:bg-[var(--fg)] hover:enabled:text-[var(--tb-porcelain)] disabled:cursor-default disabled:opacity-50"
          >
            {isLoading ? "Carregando..." : "Carregar mais"}
          </button>
        ) : null}
      </div>
    </div>
  );
}
